import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ToggleRight, AlertCircle } from "lucide-react";

const CATEGORIES = ["core", "ordering", "advanced", "future"];

export default function FeatureControls() {
  const [features, setFeatures] = useState([]);
  const load = () => api.get("/features/all").then(({data}) => setFeatures(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    try {
      await api.patch("/features", { key, enabled });
      toast.success(`${key} ${enabled ? "enabled" : "disabled"}`);
      load();
      // refresh feature context globally
      setTimeout(() => window.dispatchEvent(new Event("features-changed")), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  return (
    <div data-testid="feature-controls">
      <div className="flex items-center gap-3 mb-6">
        <ToggleRight className="w-7 h-7 text-[#D4AF37]"/>
        <div>
          <h1 className="font-heading text-3xl text-[#FAF9F6]">Feature Controls</h1>
          <p className="text-sm text-[#FAF9F6]/50">Enable modules as your business grows. Hidden modules disappear from navigation and dashboard.</p>
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const items = features.filter((f) => f.category === cat);
        if (!items.length) return null;
        return (
          <section key={cat} className="mb-8" data-testid={`section-${cat}`}>
            <h3 className="font-accent text-xs uppercase text-[#D4AF37] mb-3 tracking-widest">{cat}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {items.map((f) => (
                <div key={f.key} className="admin-card p-5 flex items-start gap-4" data-testid={`feature-${f.key}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-heading text-lg text-[#FAF9F6]">{f.name}</h4>
                      {f.recommended_mvp && <span className="badge-fresh">MVP</span>}
                      {f.advanced && <span className="badge-royal">ADVANCED</span>}
                    </div>
                    <p className="text-xs text-[#FAF9F6]/60 leading-relaxed">{f.description}</p>
                    {f.dependencies?.length > 0 && (
                      <div className="text-[10px] text-[#E8A87C] mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3"/> requires: {f.dependencies.join(", ")}
                      </div>
                    )}
                  </div>
                  <Switch
                    data-testid={`toggle-${f.key}`}
                    checked={f.enabled}
                    onCheckedChange={(v) => toggle(f.key, v)}
                  />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
